import LifepointsLogo from 'components/assets/lifepoints-logo.svg';
import Button from 'components/atoms/Button/Button';
import Card from 'components/atoms/Card/Card';
import Heading from 'components/atoms/Heading/Heading';
import Overlay from 'components/atoms/Overlay/Overlay';
import RadioButton from 'components/atoms/RadioButtons/RadioButton';
import RadioGroup from 'components/atoms/RadioButtons/RadioGroup';
import Txt from 'components/atoms/Txt/Txt';
import { useRef } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import { BarcModal_cookiesRef$key } from './__generated__/BarcModal_cookiesRef.graphql';
import { BarcModalUpdateCookiesMutation } from './__generated__/BarcModalUpdateCookiesMutation.graphql';
import classes from './BarcModal.module.scss';

export type Props = {
  cookiesRef: BarcModal_cookiesRef$key;
  onClose: () => void;
  onSave: () => void;
};

const cookiesFragement = graphql`
  fragment BarcModal_cookiesRef on Query {
    cookies {
      edges {
        node {
          cookieId
          description
          consent {
            status
          }
        }
      }
    }
  }
`;

const updateCookiesMutation = graphql`
  mutation BarcModalUpdateCookiesMutation($input: [CookieConsentInput!]!) {
    updateUserConsent(input: $input) {
      edges {
        node {
          cookieId
          description
          consent {
            status
          }
        }
      }
    }
  }
`;

const COOKIES_INFO: Record<string, { expiry: number; types: string[] }> = {
  comscoreadtrack: {
    expiry: 720,
    types: ['Advertising Exposure and Measurement'],
  },
  insightexpress: {
    expiry: 730,
    types: ['Advertising Targeting Research', 'Advertising Exposure and Measurement'],
  },
  mecwebtrack: {
    expiry: 1825,
    types: ['Data Matching and Enrichment'],
  },
  metrixlab: {
    expiry: 365,
    types: ['Advertising Exposure and Measurement'],
  },
};

export default function BarcModal({ onClose, onSave, cookiesRef }: Props) {
  const data = useFragment(cookiesFragement, cookiesRef);
  const formRef = useRef<HTMLFormElement>(null);
  const [updateCookies, updateInProgress] =
    useMutation<BarcModalUpdateCookiesMutation>(updateCookiesMutation);

  function handleSave() {
    if (formRef.current) {
      const formData = new FormData(formRef.current);
      updateCookies({
        variables: {
          input: Array.from(formData).map(([cookieId, consent]) => ({
            cookieId,
            consented: consent === 'accept',
          })),
        },
        onCompleted: onSave,
        onError: onClose,
      });
    }
  }

  return (
    <Overlay onBackgroundInteraction={onClose}>
      <Card className={classes.container}>
        <Button buttonType="text" className={classes.closeButton} onClick={onClose}>
          ×
        </Button>
        <LifepointsLogo alt="Lifepoints Logo" width={151} height={31} className={classes.logo} />
        <Heading level={3} className={classes.header}>
          Unlock additional survey opportunities
        </Heading>
        <Txt component="p" className={classes.intro}>
          Gain access to exclusive surveys by opting in for our Advertising Research opportunities.
        </Txt>
        <Txt component="p">
          With your permission, we, selected clients, and/or our technology partners will use
          &quot;Behavioural and Advertising Research Cookies&quot; to attempt to match you to
          surveys relating to advertising you’ve seen on websites you visit.
        </Txt>
        <Txt component="p">
          You can accept or decline these cookies below, though bear in mind declining these may
          affect the number of surveys we&apos;re able to offer you. Come back later to edit your
          preferences at any time by going into My Account and then Privacy Settings page.
        </Txt>
        <div className={classes.cookieList}>
          <form ref={formRef}>
            {data.cookies?.edges
              ?.filter(
                (edge) =>
                  edge?.node?.consent?.status === 'NEW' ||
                  edge?.node?.consent?.status === 'EXPIRED',
              )
              .map((edge) => (
                <div key={edge.node.cookieId} className={classes.cookie}>
                  <div className={classes.cookieHeader}>
                    <Txt className={classes.cookieName} skipTranslation>
                      {edge.node.description}
                    </Txt>
                    <Txt className={classes.cookieExpiry}>
                      Permission expires in{' '}
                      {{ expiration: COOKIES_INFO?.[edge.node.cookieId]?.expiry ?? '365' }} days
                    </Txt>
                  </div>
                  <div className={classes.cookieTypes}>
                    {COOKIES_INFO?.[edge.node.cookieId]?.types.map((type) => (
                      <Txt key={type} component="p" className={classes.cookieType} skipTranslation>
                        {type}
                      </Txt>
                    ))}
                  </div>
                  <RadioGroup className={classes.cookieConsentGroup}>
                    <RadioButton
                      value={'accept'}
                      name={edge.node.cookieId}
                      label={'Accept'}
                      className={classes.cookieConsentRadio}
                      inputClassName={classes.cookieConsentRadioInput}
                      labelClassName={classes.cookieConsentRadioLabel}
                    />
                    <RadioButton
                      value={'decline'}
                      name={edge.node.cookieId}
                      label={'Decline'}
                      className={classes.cookieConsentRadio}
                      inputClassName={classes.cookieConsentRadioInput}
                      labelClassName={classes.cookieConsentRadioLabel}
                    />
                  </RadioGroup>
                </div>
              ))}
          </form>
        </div>
        <Button
          buttonType="primary"
          buttonSize="large"
          className={classes.ctaButton}
          onClick={handleSave}
          onAuxClick={handleSave}
          disabled={updateInProgress}
        >
          <Txt>Save</Txt>
        </Button>
      </Card>
    </Overlay>
  );
}
